import { useRef, useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { MdUploadFile } from 'react-icons/md';
import { Size } from '@Components/designConstants';
import { Button } from '../../../Components/Button';
import { InteractiveDiv } from '../../../Components/InteractiveEl';
import { SegmentedControlButton } from '../../../Components/SegmentedControlButton';
import { BatchJob, parseIds } from '../CommerceScraperUtils';
import { BatchProgress } from '../ScraperResults';
import { ImportMode, ImportModeType } from './scorecard-import-constants';
import { runAsinBatchScore } from './scorecard-import-utils';
import { AuditedProduct } from './scoring-engine-constants';
import { processBlisspointCSV } from './scoring-engine-utils';
import { formatAiError } from './title-compliance-utils';

const IMPORT_MODE_OPTIONS = [
  { key: ImportMode.ASIN, label: 'Look Up ASINs' },
  { key: ImportMode.CSV, label: 'Upload CSV' }
] as const;

interface ScorecardImportPanelProps {
  tld: string;
  onProductsLoaded: (products: AuditedProduct[], fileName: string) => void;
}

export const ScorecardImportPanel = ({ tld, onProductsLoaded }: ScorecardImportPanelProps) => {
  const [importMode, setImportMode] = useState<ImportModeType>(ImportMode.ASIN);
  const [asinText, setAsinText] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [running, setRunning] = useState(false);
  const [batchJob, setBatchJob] = useState<BatchJob | null>(null);
  const [message, setMessage] = useState<{ text: string; variant: 'success' | 'danger' } | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const asinCount = parseIds(asinText).length;

  const runAsinLookup = async () => {
    const asins = parseIds(asinText);
    if (asins.length === 0) return;

    setRunning(true);
    setMessage(null);
    setBatchJob(null);
    try {
      const products = await runAsinBatchScore(asins, tld, setBatchJob);
      if (products.length === 0) {
        setMessage({
          text: 'No listings could be scored from the ASINs provided.',
          variant: 'danger'
        });
      } else {
        onProductsLoaded(products, `${asins.length} ASIN${asins.length === 1 ? '' : 's'}`);
        setMessage({
          text: `Successfully scored ${products.length} of ${asins.length} listings.`,
          variant: 'success'
        });
      }
    } catch (e) {
      setMessage({
        text: formatAiError(e),
        variant: 'danger'
      });
    } finally {
      setRunning(false);
    }
  };

  const handleCsvFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setMessage({
        text: 'Invalid format. Please select or drop a standard .csv scraper export.',
        variant: 'danger'
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result;
      if (typeof text !== 'string') {
        setMessage({ text: 'Unable to read the selected file.', variant: 'danger' });
        return;
      }
      const products = processBlisspointCSV(text);
      if (products.length === 0) {
        setMessage({
          text: 'No records could be mapped to an ASIN. Verify the CSV headers match the scraper export format.',
          variant: 'danger'
        });
        return;
      }
      onProductsLoaded(products, file.name);
      setMessage({
        text: `Successfully loaded ${products.length} scraper rows from ${file.name}.`,
        variant: 'success'
      });
    };
    reader.onerror = () => {
      setMessage({ text: 'Unable to read the selected file.', variant: 'danger' });
    };
    reader.readAsText(file);
  };

  return (
    <div className='scorecard-import-panel'>
      <SegmentedControlButton
        size={Size.LG}
        selectedOption={importMode}
        onChange={setImportMode}
        options={IMPORT_MODE_OPTIONS}
      />

      {importMode === ImportMode.ASIN ? (
        <div className='scorecard-import-asin'>
          <Form.Control
            as='textarea'
            rows={4}
            placeholder='Enter ASINs, one per line...'
            value={asinText}
            onChange={e => setAsinText(e.target.value)}
            disabled={running}
          />
          <div className='scorecard-import-actions'>
            <span className='scorecard-import-count'>
              {asinCount} ASIN{asinCount === 1 ? '' : 's'}
            </span>
            <Button size={Size.LG} onClick={runAsinLookup} disabled={running || asinCount === 0}>
              {running
                ? 'Scoring…'
                : `Score ${asinCount || ''} Listing${asinCount === 1 ? '' : 's'}`}
            </Button>
          </div>
          {batchJob && <BatchProgress job={batchJob} />}
        </div>
      ) : (
        <InteractiveDiv
          className={`scorecard-import-dropzone${isDragging ? ' is-dragging' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={e => {
            e.preventDefault();
            setIsDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file) {
              handleCsvFile(file);
            }
          }}
        >
          <MdUploadFile className='scorecard-import-dropzone-icon' />
          <span className='scorecard-import-dropzone-title'>Drop your scraper CSV here</span>
          <span className='scorecard-import-dropzone-subtitle'>or click to browse files</span>
          <input
            ref={fileInputRef}
            type='file'
            accept='.csv'
            className='scorecard-import-dropzone-input'
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) {
                handleCsvFile(file);
              }
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
          />
        </InteractiveDiv>
      )}

      {message && (
        <Alert variant={message.variant} className='scorecard-import-message'>
          {message.text}
        </Alert>
      )}
    </div>
  );
};
